// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@steiner/App/Components/Product/ProductPriceAndActions";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import ProductListProductInformation, {
    ProductListProductInformationStyles,
} from "@steiner/Overrides/Widgets/ProductList/ProductListProductInformation";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    // XNG-Changes
    priceGridItem?: GridItemProps;
    productPriceAndActionsStyles?: ProductPriceAndActionsStyle;
    productInformationStyles?: ProductListProductInformationStyles;
    productListProductImage?: ProductListProductImageStyles;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    // XNG-Change: restructure if its tablet (Price within the info, compare & show add to list from actions)
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);

    return (
        <StyledWrapper {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
            <StyledWrapper {...styles.leftColumnGridItem}>
                {props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
                <ProductListProductImage
                    showImage={props.showImage}
                    showCompare={false}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                    // XNG-Changes
                    extendedStyles={styles.productListProductImage}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.infoGridItem}>
                <ProductListProductInformation
                    showBrand={props.showBrand}
                    showTitle={props.showTitle}
                    showPartNumbers={props.showPartNumbers}
                    showAvailability={isTabletScreen && props.showAvailability}
                    showAttributes={props.showAttributes}
                    showImageBadges={props.showImageBadges}
                    maxImageBadges={props.maximumNumberOfImageBadges}
                    showTextBadges={props.showTextBadges}
                    maxTextBadges={props.maximumNumberOfTextBadges}
                    {...{
                        extendedStyles: styles.productInformationStyles,
                        showPrice: isTabletScreen && props.showPrice,
                    }}
                />
            </StyledWrapper>
            {/* XNG-Changes: hide on Tablet/Mobile */}
            {!isTabletScreen && (
                <StyledWrapper {...styles.priceGridItem}>
                    <ProductPriceAndActions
                        showAvailability={props.showAvailability}
                        showAddToList={false}
                        showPrice={props.showPrice}
                        showCompare={false}
                        showAddToCartActions={false}
                        view="List"
                        containerClassName="desktop-pricing-container"
                        extendedStyles={styles.productPriceAndActionsStyles}
                    />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.actionsGridItem}>
                <ProductPriceAndActions
                    showAvailability={false}
                    showAddToList={props.showAddToList}
                    showPrice={false}
                    showCompare={props.showCompare}
                    showAddToCartActions={true}
                    view="List"
                    extendedStyles={styles.productPriceAndActionsStyles}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.searchScoreDataGridItem}>
                <ProductSearchScoreData />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductCard);
